import type { InventoryItem } from '../types/inventory';
import {
  barcodeToSvgString,
  LABEL_BARCODE_SVG_MAX_HEIGHT_MM,
  LABEL_BARCODE_PRINT_JS_OPTIONS,
} from './inventoryLabelBarcode';
import { escapeHtml, openPrintWindow, PRINT_POPUP_BLOCKED_MESSAGE } from './printWindow';

export interface InventoryLabelPrintRow {
  productCodeUnique: string;
  materialLabel: string;
  salePrice: number;
  weightGrams: number | null;
}

/**
 * Etiqueta 94×11 mm: adhesivo 4,4 cm (izq., sin imprimir) + info 2,5 cm + código de barras 2,5 cm.
 * 44 + 25 + 25 = 94 mm.
 */
export const LABEL_PRINT_WIDTH_MM = 94;
export const LABEL_PRINT_HEIGHT_MM = 11;
/** Franja adhesiva a la izquierda; sin contenido impreso. */
export const LABEL_SEG_ADHESIVE_MM = 44;
/** Info: material + precio (2,5 cm). */
export const LABEL_SEG_INFO_MM = 25;
/** Código de barras + referencias (2,5 cm). */
export const LABEL_SEG_BARCODE_MM = 25;

function formatPriceBs(price: number): string {
  const n = Number(price);
  const s = n.toLocaleString('es-BO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return `Bs. ${s}`;
}

function formatWeight(weightGrams: number | null): string {
  if (weightGrams == null || Number.isNaN(Number(weightGrams))) return '—';
  return Number(weightGrams).toLocaleString('es-BO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

/**
 * Abre ventana de impresión: todo el lote en una sola página lógica (94 mm × N×11 mm)
 * para que el rollo avance seguido sin “una hoja enorme por etiqueta”. Barcode = CODE128.
 */
export function openInventoryLabelsPrintWindow(rows: InventoryLabelPrintRow[]): void {
  if (rows.length === 0) return;

  const sheetHeightMm = rows.length * LABEL_PRINT_HEIGHT_MM;

  const labelsHtml = rows
    .map((row) => {
      const svg = barcodeToSvgString(row.productCodeUnique, LABEL_BARCODE_PRINT_JS_OPTIONS);
      return `
      <div class="label-page">
        <div class="label-box">
          <div class="label-seg-adhesive" aria-hidden="true"></div>
          <div class="label-seg-info">
            <div class="mat">${escapeHtml(row.materialLabel)}</div>
            <div class="price">${escapeHtml(formatPriceBs(row.salePrice))}</div>
          </div>
          <div class="label-seg-barcode">
            <div class="barcode-wrap">${svg}</div>
            <div class="subbar">
              <span class="weight">${escapeHtml(formatWeight(row.weightGrams))}</span>
              <span class="code">${escapeHtml(row.productCodeUnique)}</span>
            </div>
          </div>
        </div>
      </div>`;
    })
    .join('');

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Etiquetas</title>
  <style>
    * { box-sizing: border-box; }
    @page {
      size: ${LABEL_PRINT_WIDTH_MM}mm ${sheetHeightMm}mm;
      margin: 0;
    }
    html {
      margin: 0;
      padding: 0;
    }
    /* Sin padding: si no, Chrome muestra cada etiqueta como mancha en una hoja grande */
    body {
      margin: 0;
      padding: 0;
      font-family: 'Times New Roman', Times, serif;
      background: #fff;
      color: #000;
    }
    .label-sheet {
      width: ${LABEL_PRINT_WIDTH_MM}mm;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      flex-wrap: nowrap;
    }
    /* Una fila = una etiqueta; varias filas seguidas en la misma @page */
    .label-page {
      width: ${LABEL_PRINT_WIDTH_MM}mm;
      height: ${LABEL_PRINT_HEIGHT_MM}mm;
      margin: 0;
      padding: 0;
      overflow: hidden;
      flex-shrink: 0;
      page-break-inside: avoid;
      break-inside: avoid;
    }
    .label-box {
      width: 100%;
      height: 100%;
      border: none;
      display: flex;
      flex-direction: row;
      align-items: stretch;
      padding: 0.5mm 0.6mm;
      overflow: hidden;
    }
    .label-seg-adhesive {
      flex: ${LABEL_SEG_ADHESIVE_MM} 1 0%;
      min-width: 0;
    }
    .label-seg-info {
      flex: ${LABEL_SEG_INFO_MM} 1 0%;
      min-width: 0;
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 0.15mm;
      padding-right: 0.8mm;
    }
    .label-seg-barcode {
      flex: ${LABEL_SEG_BARCODE_MM} 1 0%;
      min-width: 0;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      justify-content: center;
      padding: 0 0.8mm;
    }
    .mat {
      font-style: italic;
      font-size: 7pt;
      line-height: 1.1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .price {
      font-size: 7pt;
      font-style: normal;
      line-height: 1.1;
    }
    .barcode-wrap {
      flex: 1 1 auto;
      min-height: 0;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      max-width: 100%;
    }
    .barcode-wrap svg { max-width: 100%; height: auto; max-height: ${LABEL_BARCODE_SVG_MAX_HEIGHT_MM}mm; display: block; }
    .subbar {
      flex: 0 0 auto;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: baseline;
      width: 100%;
      font-size: 5.5pt;
    }
    .weight { flex: 0 0 auto; }
    .code { text-decoration: underline; text-align: right; }
    /* Pantalla: centrar tira en la ventana */
    @media screen {
      body {
        width: ${LABEL_PRINT_WIDTH_MM}mm;
        margin: 0 auto;
      }
    }
    /* Impresión: alinear al borde; evitar que un body centrado/anchuro desplace la tira */
    @media print {
      html, body {
        margin: 0;
        padding: 0;
        width: auto;
        max-width: none;
      }
      .label-sheet {
        margin: 0;
      }
    }
  </style>
</head>
<body>
  <div class="label-sheet">
  ${labelsHtml}
  </div>
</body>
</html>`;

  const printWindow = openPrintWindow({
    html,
    size: 'labels',
    autoPrint: true,
    windowName: 'printLabels',
  });

  if (!printWindow) {
    window.alert(PRINT_POPUP_BLOCKED_MESSAGE);
  }
}

/** Una etiqueta desde fila de almacén/sucursal (Gestión en almacén / Gestión en sucursal). */
export function printInventoryUnitLabel(item: InventoryItem): void {
  openInventoryLabelsPrintWindow([
    {
      productCodeUnique: String(item.id),
      materialLabel: item.material || '—',
      salePrice: item.price ?? 0,
      weightGrams: item.weightGrams ?? null,
    },
  ]);
}
